import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, Download, FileSignature, Lock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { AxiosInstance } from "axios";
import { Btn, ErrorNote, Field, Input, MessageBox, Panel, Select, Textarea } from "@/components/kmt/ui";
import { TimeSelect } from "@/components/kmt/TimeSelect";
import { RideForm } from "@/components/kmt/RideForm";
import { FareForm } from "./FareForm";
import { downloadFile, errMsg } from "@/lib/api";
import { METHODS, METHOD_LABEL, SERVICE_LABEL, STAGES, centralParts, ct, prettyPhone, todayCentral, usd } from "@/lib/format";

type P = { b: any; api: AxiosInstance; onChange: (b?: any) => void };
const EDITABLE = ["requested", "quoted", "agreement_pending"];
const CLOSED = ["completed", "cancelled", "no_show", "declined"];

export function TripPanel({ b, api, onChange }: P) {
  const [edit, setEdit] = useState(false);
  const [stamp, setStamp] = useState(false);
  const p = centralParts(b.pickup_at);
  return (
    <Panel title="Trip" data-testid="trip-panel" actions={EDITABLE.includes(b.status) && (
      <Btn variant="ghost" onClick={() => setEdit(!edit)} data-testid="trip-edit-toggle">{edit ? "Close" : "Edit trip"}</Btn>)}>
      {edit ? (
        <RideForm hideContact owner submitLabel="Save trip" initial={{ ...b, pickup_date: p.date, pickup_time: p.time }}
          onSubmit={async (x) => { onChange((await api.put(`/bookings/${b.id}`, x)).data); setEdit(false); toast.success("Trip updated"); }} />
      ) : (
        <dl className="grid gap-x-8 gap-y-3 text-[15px] sm:grid-cols-2">
          <Item k="Service" v={`${SERVICE_LABEL[b.service]} · ${b.trip_type === "round_trip" ? "Round trip" : "One way"}`} />
          <Item k="Pickup" v={ct(b.pickup_at)} testid="trip-pickup" />
          <Item k="From" v={b.pickup_address} />
          <Item k="To" v={b.dropoff_address} />
          {b.stops.map((s: string, i: number) => <Item key={i} k={`Stop ${i + 1}`} v={s} />)}
          <Item k="Notes" v={b.notes || "—"} />
          <Item k="Requested at" v={`${ct(b.requested_at)}${b.requested_at !== b.requested_at_system ? " (edited)" : ""}`} testid="trip-requested-at" />
          <Item k="Source" v={b.source === "web" ? "Website request" : "Phone / manual"} />
        </dl>
      )}
      <div className="mt-5">
        <Btn variant="quiet" onClick={() => setStamp(!stamp)} data-testid="requested-at-toggle" className="px-0">Change request time stamp</Btn>
        {stamp && <RequestedAtForm b={b} api={api} onChange={(x) => { onChange(x); setStamp(false); }} />}
      </div>
    </Panel>
  );
}

function RequestedAtForm({ b, api, onChange }: P) {
  const p = centralParts(b.requested_at);
  const [f, setF] = useState({ requested_date: p.date, requested_time: p.time, reason: "" });
  const [err, setErr] = useState("");
  const save = async () => {
    try {
      onChange((await api.patch(`/bookings/${b.id}/requested-at`, f)).data);
      toast.success("Request time updated and logged");
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div className="mt-3 grid gap-4 rounded-md border border-gold/20 p-4 md:grid-cols-3">
      <Field label="Date"><Input type="date" value={f.requested_date} onChange={(e) => setF({ ...f, requested_date: e.target.value })} data-testid="requested-at-date" /></Field>
      <Field label="Time"><TimeSelect value={f.requested_time} onChange={(v) => setF({ ...f, requested_time: v })} testid="requested-at-time" /></Field>
      <Field label="Reason (logged)"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="requested-at-reason" placeholder="Phone booking" /></Field>
      <div className="md:col-span-3"><ErrorNote msg={err} /><Btn onClick={save} data-testid="requested-at-save-btn" className="mt-2">Save stamp</Btn></div>
    </div>
  );
}

const Item = ({ k, v, testid }: { k: string; v: string; testid?: string }) => (
  <div><dt className="text-[14px] text-mist/60">{k}</dt><dd data-testid={testid} className="break-words">{v}</dd></div>
);

export function QuotePanel({ b, api, onChange }: P) {
  const q = b.quote;
  const [open, setOpen] = useState(!q);
  const canQuote = EDITABLE.includes(b.status);
  return (
    <Panel title="Quote" data-testid="quote-panel" actions={q && canQuote && (
      <Btn variant="ghost" onClick={() => setOpen(!open)} data-testid="quote-new-toggle">{open ? "Close calculator" : "Issue new quote"}</Btn>)}>
      {b.quote_stale && (
        <p role="alert" className="mb-4 flex gap-2 rounded-md border border-amber-300/50 bg-amber-400/10 p-3 text-[14px] text-amber-100" data-testid="quote-stale-warning">
          <AlertTriangle size={18} /> Trip details changed after this quote. Issue a new quote.
        </p>
      )}
      {q && (
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="flex items-center gap-2 text-[15px]"><Lock size={16} className="text-gold" /> {q.number} · locked {ct(q.issued_at)} · rules v{q.rule_version}</p>
            <p className="font-display text-3xl" data-testid="quote-total">{usd(q.breakdown.total)}</p>
          </div>
          <p className="text-[14px] text-gold" data-testid="quote-deposit-rule">{q.deposit.rule} · due to confirm {usd(q.deposit.deposit_amount)}{q.deposit.balance_due_at && ` · balance by ${ct(q.deposit.balance_due_at)}`}</p>
          <Btn variant="ghost" className="self-start" data-testid="quote-pdf-btn" onClick={() => downloadFile(api, `/quotes/${q.id}/pdf`, `${q.number}.pdf`).catch((e) => toast.error(errMsg(e)))}>
            <Download size={18} /> Quote PDF
          </Btn>
          <MessageBox text={b.quote_text} phone={b.phone} testid="quote-message" />
        </div>
      )}
      {canQuote && open && (
        <FareForm service={b.service} lockService onIssue={async (p) => { onChange((await api.post(`/bookings/${b.id}/quotes`, p)).data); setOpen(false); toast.success("Quote locked"); }} />
      )}
    </Panel>
  );
}

export function ConfirmPanel({ b, api, onChange }: P) {
  const [err, setErr] = useState("");
  const m = b.money;
  const idx = STAGES.findIndex((s) => s.value === b.status);
  const next = idx >= 0 ? STAGES.slice(idx + 1) : [];
  const move = async (status: string) => {
    setErr("");
    try {
      onChange((await api.post(`/bookings/${b.id}/status`, { status })).data);
      toast.success("Stage updated");
    } catch (e) { setErr(errMsg(e)); }
  };
  const Check = ({ ok, label, testid }: { ok: boolean; label: string; testid: string }) => (
    <li className="flex items-center gap-3 text-[15px]" data-testid={testid} data-ok={ok}>
      {ok ? <CheckCircle2 className="text-emerald-300" size={20} /> : <XCircle className="text-red-300" size={20} />} {label}
    </li>
  );
  if (!b.quote) {
    return (
      <Panel title="Confirmation check" data-testid="confirm-panel">
        <p className="text-[15px] text-mist/75" data-testid="confirm-needs-quote">Issue a quote first. Payment checks and balances appear once the quote is locked.</p>
      </Panel>
    );
  }
  return (
    <Panel title="Confirmation check" data-testid="confirm-panel">
      <ul className="flex flex-col gap-3">
        <Check ok={b.checks.agreement_on_file} label={`Master Agreement ${b.agreement_version} on file`} testid="check-agreement" />
        <Check ok={b.checks.owner_approved} label="Approved by Kyia" testid="check-approved" />
        <Check ok={b.checks.payment_sufficient} label={`Verified payment covers ${usd(m.required_now)}`} testid="check-payment" />
      </ul>
      <dl className="mt-5 grid grid-cols-2 gap-3 text-[15px]">
        <Money k="Total" v={m.total} t="money-total" />
        <Money k="Paid (net)" v={m.paid} t="money-paid" />
        <Money k="Required now" v={m.required_now} t="money-required" />
        <Money k="Shortfall" v={m.shortfall} t="money-shortfall" warn={m.shortfall > 0} />
        <Money k="Balance remaining" v={m.remaining} t="money-remaining" />
        <div><dt className="text-[14px] text-mist/60">Balance due</dt><dd data-testid="money-balance-due">{m.balance_due_at ? ct(m.balance_due_at) : "With deposit (100%)"}</dd></div>
      </dl>
      {m.overdue && <p role="alert" className="mt-4 rounded-md border border-red-300/50 bg-red-500/15 p-3 text-[14px] text-red-100" data-testid="money-overdue-alert">Balance is overdue. The ride will not cancel automatically.</p>}
      <ErrorNote msg={err} />
      {next.length > 0 && !CLOSED.includes(b.status) && (
        <div className="mt-5 flex flex-wrap gap-3">
          {next.map((s) => (
            <Btn key={s.value} variant={s.value === "confirmed" ? "primary" : "ghost"} onClick={() => move(s.value)} data-testid={`stage-move-${s.value}`}>{s.label}</Btn>
          ))}
        </div>
      )}
    </Panel>
  );
}

const Money = ({ k, v, t, warn }: { k: string; v: number; t: string; warn?: boolean }) => (
  <div><dt className="text-[14px] text-mist/60">{k}</dt><dd data-testid={t} className={warn ? "text-red-200" : ""}>{usd(v)}</dd></div>
);

export function AgreementPanel({ b, api, onChange }: P) {
  const [link, setLink] = useState<any>(null);
  const a = b.agreement;
  const create = async () => {
    try {
      setLink((await api.post("/agreements/links", { booking_id: b.id, origin: window.location.origin })).data);
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const download = () => downloadFile(api, `/agreements/${a.id}/pdf`, `KMT-Agreement-${b.ref}.pdf`).catch((e) => toast.error(errMsg(e)));
  return (
    <Panel title="Master Agreement" data-testid="agreement-panel">
      {a ? (
        <div className="flex flex-col gap-3 text-[15px]">
          <p className="flex items-center gap-2 text-emerald-200" data-testid="agreement-on-file"><FileSignature size={18} /> {a.version} signed {ct(a.signed.signed_at)}</p>
          {a.signed.phone_matches === false && (
            <p role="alert" className="rounded-md border border-amber-300/50 bg-amber-400/10 p-3 text-[14px] text-amber-100" data-testid="agreement-phone-mismatch">
              Signer typed {prettyPhone(a.signed.phone)}, which is not on this customer's record.
            </p>
          )}
          <Btn variant="ghost" className="self-start" onClick={download} data-testid="agreement-pdf-btn"><Download size={18} /> Signed PDF</Btn>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-[15px] text-mist/80" data-testid="agreement-missing">No signed {b.agreement_version} agreement for {prettyPhone(b.phone)} yet.</p>
          <Btn onClick={create} className="self-start" data-testid="agreement-create-link-btn">Create signing link</Btn>
          {link && <><p className="break-all text-[14px] text-gold" data-testid="agreement-link-url">{link.url}</p><MessageBox text={link.sms_text} phone={link.phone} testid="agreement-message" /></>}
        </div>
      )}
    </Panel>
  );
}

export function PaymentsPanel({ b, api, onChange }: P) {
  const [f, setF] = useState({ kind: "payment", method: "zelle", method_note: "", amount: "", reference: "", received_date: todayCentral() });
  const [err, setErr] = useState("");
  const save = async () => {
    setErr("");
    try {
      onChange((await api.post(`/bookings/${b.id}/payments`, { ...f, amount: Number(f.amount) })).data);
      setF({ ...f, amount: "", reference: "", method_note: "" });
      toast.success(f.kind === "refund" ? "Refund recorded" : "Payment recorded");
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Panel title="Payments" data-testid="payments-panel">
      {b.payments.length > 0 ? (
        <ul className="mb-6 divide-y divide-gold/15 text-[15px]" data-testid="payments-list">
          {b.payments.map((p: any) => (
            <li key={p.id} className="flex flex-wrap justify-between gap-2 py-3">
              <span>{p.kind === "refund" ? "Refund" : "Payment"} · {METHOD_LABEL[p.method]}{p.method_note && ` (${p.method_note})`} · {p.received_date}{p.reference && ` · ref ${p.reference}`}</span>
              <span className={p.kind === "refund" ? "text-red-200" : "text-emerald-200"}>{p.kind === "refund" ? "-" : ""}{usd(p.amount)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="mb-6 text-[15px] text-mist/65">No payments recorded.</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Type"><Select options={[{ value: "payment", label: "Payment received" }, { value: "refund", label: "Refund sent" }]} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="payment-kind-select" /></Field>
        <Field label="Method"><Select options={METHODS} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="payment-method-select" /></Field>
        {f.method === "other" && <Field label="Describe the method" className="sm:col-span-2"><Input value={f.method_note} onChange={(e) => setF({ ...f, method_note: e.target.value })} data-testid="payment-method-note" /></Field>}
        <Field label="Amount actually received ($)"><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
        <Field label="Reference"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="payment-reference-input" /></Field>
        <Field label="Date"><Input type="date" value={f.received_date} onChange={(e) => setF({ ...f, received_date: e.target.value })} data-testid="payment-date-input" /></Field>
      </div>
      <ErrorNote msg={err} />
      <Btn onClick={save} disabled={!f.amount} className="mt-4" data-testid="payment-save-btn">Record {f.kind}</Btn>
    </Panel>
  );
}

export function InvoicePanel({ b, api }: P) {
  if (!b.quote) return null;
  return (
    <Panel title="Booking Confirmation & Invoice" data-testid="invoice-panel">
      <Btn variant="ghost" className="mb-4" data-testid="invoice-pdf-btn" onClick={() => downloadFile(api, `/bookings/${b.id}/invoice.pdf`, `${b.ref}-invoice.pdf`).catch((e) => toast.error(errMsg(e)))}>
        <Download size={18} /> Invoice PDF
      </Btn>
      <MessageBox text={b.invoice_text} phone={b.phone} testid="invoice-message" />
    </Panel>
  );
}

const KINDS = [{ value: "cancelled", label: "Customer cancelled" }, { value: "no_show", label: "No-show (provisional rate)" }, { value: "declined", label: "Declined by KMT (full refund)" }];

export function CancelPanel({ b, api, onChange }: P) {
  const [kind, setKind] = useState("cancelled");
  const [note, setNote] = useState("");
  const [terms, setTerms] = useState<any>(null);
  const [err, setErr] = useState("");
  const c = b.cancellation;
  if (c) {
    const refundLeft = b.refund_remaining ?? 0;
    return (
      <Panel title="Closed" data-testid="cancel-summary">
        <Terms t={c} />
        <p className="mt-3 text-[15px]" data-testid="refund-remaining">Refund still to send: <strong className="text-gold">{usd(refundLeft)}</strong> (record it under Payments as a refund)</p>
      </Panel>
    );
  }
  if (CLOSED.includes(b.status)) return null;
  const run = async (preview: boolean) => {
    setErr("");
    try {
      const r = (await api.post(`/bookings/${b.id}/cancel`, { kind, note, preview })).data;
      if (preview) setTerms(r.terms);
      else { onChange(r.booking); toast.success("Booking closed"); }
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Panel title="Cancel, no-show or decline" data-testid="cancel-panel">
      <div className="grid gap-4">
        <Field label="Outcome"><Select options={KINDS} value={kind} onChange={(e) => { setKind(e.target.value); setTerms(null); }} data-testid="cancel-kind-select" /></Field>
        <Field label="Note"><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="cancel-note" /></Field>
        <ErrorNote msg={err} />
        {terms && <Terms t={terms} />}
        <div className="flex flex-wrap gap-3">
          <Btn variant="ghost" onClick={() => run(true)} data-testid="cancel-preview-btn">Work out amounts</Btn>
          {terms && <Btn variant="danger" onClick={() => run(false)} data-testid="cancel-confirm-btn">Close booking</Btn>}
        </div>
      </div>
    </Panel>
  );
}

const Terms = ({ t }: { t: any }) => (
  <div className="rounded-md border border-gold/25 p-4 text-[15px]" data-testid="cancel-terms">
    <p className="text-gold">{t.rule}{t.provisional && " — provisional"}</p>
    <p className="mt-1">Kept: {t.keep_pct}% = {usd(t.keep_amount)} · Kept from paid: {usd(t.kept_from_paid)}</p>
    <p data-testid="cancel-refund-amount">Refund to customer: <strong>{usd(t.refund_amount)}</strong>{t.unpaid_kept_amount > 0 && ` · Unpaid portion: ${usd(t.unpaid_kept_amount)}`}</p>
  </div>
);

export function HistoryPanel({ b }: { b: any }) {
  return (
    <Panel title="Audit log" data-testid="history-panel">
      <ul className="flex flex-col gap-3 text-[14px]">
        {(b.audit || []).map((a: any) => (
          <li key={a.id} className="border-l border-gold/40 pl-3"><span className="text-gold">{a.action}</span> · {ct(a.at)} · {a.actor}</li>
        ))}
      </ul>
    </Panel>
  );
}
